import { Component, signal, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { CoursesService, CursoCompleto, Clase } from '../../services/courses.service';
import { NotificationService } from '../../services/notification.service';
import { environment } from '../../../environments/environment.development';

interface CourseSection {
  id: number;
  title: string;
  lessons: Lesson[];
  expanded: boolean;
  descripcion?: string | null;
}

interface Lesson {
  id: number;
  title: string;
  duration: string;
  completed: boolean;
  preview: boolean;
  type: 'video' | 'audio' | 'blog' | 'podcast' | 'recursos';
  thumbnail?: string;
}

interface FAQ {
  question: string;
  answer: string;
  expanded: boolean;
}

@Component({
  selector: 'app-course-view',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './course-view.html',
  styleUrl: './course-view.css',
})
export class CourseViewComponent implements OnInit {
  private coursesService = inject(CoursesService);
  private notificationService = inject(NotificationService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  showCourseIndex = signal(false);
  currentLesson = signal(0);
  isLoading = signal(false);
  cursoId = signal<number | null>(null);
  cursoData = signal<CursoCompleto | null>(null);
  
  course = {
    id: 1,
    title: 'Loadginh',
    subtitle: 'Loading...',
    instructor: 'Loading...',
    instructorRole: 'Loading...',
    instructorBio: 'Loading...',
    thumbnail: '',
    price: 0,
    originalPrice: 0,
    discount: 'Loading...',
    rating: 0,
    totalRatings: 0,
    students: 0,
    likes: 0,
    views: 0,
    duration: '0h 0m',
    level: 'Loading...',
    sections: 0,
    lectures: 0,
    isSubscribed: true, // true = incluido en suscripción, false = debe comprarlo
    includes: [
      'Loading...'
    ]
  };

  courseSections: CourseSection[] = [
    {
      id: 1,
      title: 'Course Introduction',
      expanded: true,
      lessons: [
        { id: 1, title: 'Introduction', duration: '5:30', completed: true, preview: true, type: 'video' },
        { id: 2, title: 'Downloading TypeScript', duration: '8:45', completed: true, preview: false, type: 'video' },
        { id: 3, title: 'Settings and Preferences', duration: '12:20', completed: false, preview: false, type: 'video' },
        { id: 4, title: 'TypeScript Interface', duration: '15:10', completed: false, preview: false, type: 'video' }
      ]
    },
    {
      id: 2,
      title: 'Basics of TypeScript',
      expanded: false,
      lessons: [
        { id: 5, title: 'Absolute Basics', duration: '49:28', completed: false, preview: false, thumbnail: 'https://images.unsplash.com/photo-1516116216624-53e697fedbea?w=200&h=120&fit=crop', type: 'video' },
        { id: 6, title: 'Object Editing', duration: '28:47', completed: false, preview: false, thumbnail: 'https://images.unsplash.com/photo-1515879218367-8466d910aaa4?w=200&h=120&fit=crop', type: 'video' },
        { id: 7, title: 'Modifiers', duration: '29:32', completed: false, preview: false, thumbnail: 'https://images.unsplash.com/photo-1542831371-29b0f74f9713?w=200&h=120&fit=crop', type: 'video' }
      ]
    },
    {
      id: 3,
      title: 'Advanced Patterns',
      expanded: false,
      lessons: [
        { id: 8, title: 'Generics Deep Dive', duration: '35:20', completed: false, preview: false, type: 'video' },
        { id: 9, title: 'Decorators', duration: '42:15', completed: false, preview: false, type: 'video' }
      ]
    }
  ];

  faqs: FAQ[] = [
    {
      question: 'How long does it take to learn?',
      answer: 'The course is designed to be completed in about 8-10 weeks if you dedicate 5-7 hours per week. However, you can go at your own pace.',
      expanded: false
    },
    {
      question: 'How much does TypeScript cost?',
      answer: 'TypeScript is completely free and open source. You only pay for this course, which gives you structured learning and projects.',
      expanded: false
    },
    {
      question: 'Do I need prior programming experience?',
      answer: 'Basic JavaScript knowledge is recommended but not required. We cover all the fundamentals you need to get started.',
      expanded: false
    }
  ];

  ngOnInit() {
    window.scrollTo(0, 0);
    
    // Obtener el ID del curso desde la ruta
    this.route.params.subscribe(params => {
      const id = +params['id']; // El + convierte a número
      if (id) {
        this.cursoId.set(id);
        this.loadCurso(id);
      }
    });
  }

  /**
   * Carga los datos del curso desde el backend
   */
  loadCurso(id: number) {
    this.isLoading.set(true);

    this.coursesService.getCursoById(id).subscribe({
      next: (response) => {
        this.isLoading.set(false);

        if (response.ok && response.data) {
          this.cursoData.set(response.data);
          this.mapCursoToView(response.data);
          console.log('✅ Curso cargado exitosamente');
        } else {
          this.notificationService.error(
            'Error',
            response.msg || 'No se pudo cargar el curso'
          );
          this.router.navigate(['/dashboard/courses']);
        }
      },
      error: (error) => {
        this.isLoading.set(false);
        console.error('❌ Error al cargar curso:', error);
        this.notificationService.error(
          'Error de Conexión',
          'No se pudo conectar con el servidor'
        );
        this.router.navigate(['/dashboard/courses']);
      }
    });
  }

  /**
   * Mapea los datos del backend a la estructura del componente
   */
  mapCursoToView(curso: CursoCompleto) {
    // Actualizar información general del curso
    this.course = {
      id: curso.id_curso,
      title: curso.titulo,
      subtitle: curso.descripcion,
      instructor: curso.maestro ? `${curso.maestro.nombre} ${curso.maestro.apellido}`.trim() : 'Instructor no disponible',
      instructorRole: 'Instructor',
      instructorBio: curso.maestro ? `${curso.maestro.nombre} es un instructor experto en el área.` : 'Instructor experto en el área.',
      thumbnail: curso.foto_portada
        ? `https://storage.googleapis.com/traidinglab/cursos/portadas/${curso.foto_portada}`
        : 'https://images.unsplash.com/photo-1633356122544-f134324a6cee?w=1200&h=600&fit=crop',
      price: 0, // Precio eliminado para que no se muestre
      originalPrice: 0,
      discount: '',
      rating: 4.8,
      totalRatings: 0,
      students: 0,
      likes: 0,
      views: 0,
      duration: this.formatDuration(curso.duracion_minutos),
      level: 'Todos los niveles',
      sections: curso.modulos.length,
      lectures: curso.total_clases,
      isSubscribed: !curso.es_premium, // Si es gratuito, está "incluido"
      includes: [
        `${curso.total_clases} clases en total`,
        `${curso.modulos.length} módulos`,
        `${this.formatDuration(curso.duracion_minutos)} de contenido`,
        'Acceso por 1 año'
      ]
    };

    // Mapear módulos a secciones con sus clases
    this.courseSections = curso.modulos
      .sort((a, b) => a.orden - b.orden)
      .map((modulo, index) => ({
        id: modulo.id_modulo,
        title: modulo.titulo_modulo,
        descripcion: modulo.descripcion,
        expanded: index === 0, // Solo el primer módulo expandido
        lessons: this.mapClasesToLessons(modulo.clases)
      }));
  }

  /**
   * Mapea las clases del backend a lecciones del componente
   */
  mapClasesToLessons(clases: Clase[]): Lesson[] {
    return clases
      .sort((a, b) => a.orden - b.orden)
      .map((clase) => ({
        id: clase.id_clase,
        title: clase.titulo_clase,
        duration: this.formatClaseDuration(clase),
        type: clase.tipo_contenido as any,
        completed: clase.completada,
        preview: clase.orden === 1, // Primera clase de cada módulo es preview
        thumbnail: clase.foto_portada 
          ? `https://storage.googleapis.com/traidinglab/cursos/portadas/${clase.foto_portada}`
          : undefined
      }));
  }

  /**
   * Formatea la duración de una clase según su tipo
   */
  formatClaseDuration(clase: Clase): string {
    if (clase.tipo_contenido === 'blog') {
      if (clase.tiempo_lectura_minutos) {
        return `${clase.tiempo_lectura_minutos} min lectura`;
      }
      return 'Lectura';
    }
    
    if (clase.duracion_minutos) {
      const mins = clase.duracion_minutos;
      if (mins >= 60) {
        const hours = Math.floor(mins / 60);
        const remainingMins = mins % 60;
        return remainingMins > 0 ? `${hours}h ${remainingMins}m` : `${hours}h`;
      }
      return `${mins}m`;
    }
    
    return 'Sin duración';
  }

  /**
   * Formatea la duración en minutos a formato legible
   */
  formatDuration(minutos: number): string {
    const horas = Math.floor(minutos / 60);
    const mins = minutos % 60;
    if (horas > 0) {
      return `${horas}h ${mins > 0 ? mins + 'm' : ''}`;
    }
    return `${mins}m`;
  }

  toggleSection(sectionId: number) {
    const section = this.courseSections.find(s => s.id === sectionId);
    if (section) {
      section.expanded = !section.expanded;
    }
  }

  toggleCourseIndex() {
    this.showCourseIndex.set(!this.showCourseIndex());
  }

  toggleFAQ(index: number) {
    this.faqs[index].expanded = !this.faqs[index].expanded;
  }

  goBack() {
    this.router.navigate(['/dashboard/home']);
  }

  getTotalProgress(): number {
    let completed = 0;
    let total = 0;
    this.courseSections.forEach(section => {
      section.lessons.forEach(lesson => {
        total++;
        if (lesson.completed) completed++;
      });
    });
    return Math.round((completed / total) * 100);
  }

  startCourse() {
    if (this.cursoId()) {
      this.router.navigate(['/course', this.cursoId(), 'watch']);
    }
  }

  playLesson(lessonId: number) {
    console.log('Playing lesson:', lessonId, 'from course:', this.cursoId());
    if (this.cursoId()) {
      this.router.navigate(['/course', this.cursoId(), 'watch'], {
        queryParams: { lesson: lessonId }
      });
    }
  }

  purchaseCourse() {
    console.log('Comprar curso:', this.course.id);
    // Aquí implementarías el flujo de compra
  }
}

